import ZoomVideo from '@zoom/videosdk';

export type MeetingBrowser = 'chrome' | 'edge' | 'firefox' | 'safari' | 'other';

export type MeetingLimitation = {
  key: string;
  label: string;
};

type BrowserCapability = {
  canShareComputerAudio: boolean;
  maxVideoResolution: '720p' | '1080p';
  isFullExperience: boolean;
};

const BROWSER_CAPABILITIES: Record<MeetingBrowser, BrowserCapability> = {
  chrome: { canShareComputerAudio: true, maxVideoResolution: '1080p', isFullExperience: true },
  edge: { canShareComputerAudio: true, maxVideoResolution: '1080p', isFullExperience: true },
  safari: { canShareComputerAudio: false, maxVideoResolution: '720p', isFullExperience: false },
  firefox: { canShareComputerAudio: false, maxVideoResolution: '720p', isFullExperience: false },
  other: { canShareComputerAudio: false, maxVideoResolution: '720p', isFullExperience: false },
};

const ua = window.navigator.userAgent;

export const getMeetingBrowser = (): MeetingBrowser => {
  if (/Edg\//.test(ua)) return 'edge';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\//.test(ua) || /CriOS\//.test(ua) || /OPR\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'other';
};

export const getBrowserMeetingCapabilities = (
  browser: MeetingBrowser = getMeetingBrowser()
): BrowserCapability => BROWSER_CAPABILITIES[browser];

export type PreJoinCapabilities = BrowserCapability & {
  browser: MeetingBrowser;
  audioSupported: boolean;
  videoSupported: boolean;
  screenShareSupported: boolean;
  limitations: MeetingLimitation[];
};

export const getPreJoinCapabilities = (): PreJoinCapabilities => {
  const browser = getMeetingBrowser();
  const base = getBrowserMeetingCapabilities(browser);

  let compatibility: { audio: boolean; video: boolean; screen: boolean };
  try {
    compatibility = ZoomVideo.checkSystemRequirements();
  } catch {
    compatibility = { audio: true, video: true, screen: true };
  }

  const screenShareSupported = compatibility.screen;
  const canShareComputerAudio = base.canShareComputerAudio && screenShareSupported;

  const limitations: MeetingLimitation[] = [];
  if (!compatibility.audio) {
    limitations.push({ key: 'audio', label: 'Microphone audio may not work in this browser.' });
  }
  if (!compatibility.video) {
    limitations.push({ key: 'video', label: 'Camera video may not work in this browser.' });
  }
  if (!screenShareSupported) {
    limitations.push({ key: 'screen', label: 'Screen sharing is not available in this browser.' });
  } else if (!base.canShareComputerAudio) {
    limitations.push({
      key: 'shareAudio',
      label: "Sharing your computer's audio while screen sharing isn't available in this browser.",
    });
  }

  return {
    ...base,
    browser,
    audioSupported: compatibility.audio,
    videoSupported: compatibility.video,
    screenShareSupported,
    canShareComputerAudio,
    limitations,
  };
};
